import { useEffect, useState } from 'react';
import { PROMO_END_DATE } from '@/lib/promo';

const pad = (n: number) => n.toString().padStart(2, '0');

const getParts = () => {
  const diff = Math.max(0, PROMO_END_DATE.getTime() - Date.now());
  const totalSeconds = Math.floor(diff / 1000);
  const days = Math.floor(totalSeconds / 86400);
  const hours = Math.floor((totalSeconds % 86400) / 3600);
  const minutes = Math.floor((totalSeconds % 3600) / 60);
  const seconds = totalSeconds % 60;
  return { days, hours, minutes, seconds };
};

export const usePromoCountdown = (tickMs = 1000) => {
  const [parts, setParts] = useState(getParts);
  useEffect(() => {
    const id = window.setInterval(() => setParts(getParts()), tickMs);
    return () => window.clearInterval(id);
  }, [tickMs]);
  return parts;
};

// Hero countdown: HH:MM:SS with Arial Bold-style numbers, no labels, tight spacing
export const CountdownHero = ({ className = '' }: { className?: string }) => {
  const { days, hours, minutes, seconds } = usePromoCountdown(1000);
  const totalHours = days * 24 + hours;
  return (
    <span
      className={`inline-flex items-center gap-0 font-['Arial','Helvetica',sans-serif] font-bold tabular-nums tracking-tight ${className}`}
      aria-label={`Tempo residuo: ${pad(totalHours)} ore, ${pad(minutes)} minuti e ${pad(seconds)} secondi`}
    >
      <span>{pad(totalHours)}</span>
      <span className="mx-[-1px] opacity-60">:</span>
      <span>{pad(minutes)}</span>
      <span className="mx-[-1px] opacity-60">:</span>
      <span>{pad(seconds)}</span>
    </span>
  );
};

// Compact HH:MM display (for other small uses)
export const CountdownCompact = ({ className = '' }: { className?: string }) => {
  const { days, hours, minutes } = usePromoCountdown(30_000);
  const totalHours = days * 24 + hours;
  return (
    <span className={className} aria-label={`Tempo residuo: ${totalHours} ore e ${minutes} minuti`}>
      {pad(totalHours)}h : {pad(minutes)}m
    </span>
  );
};

// Full days/hours/minutes/seconds block (for /promozioni)
const Cell = ({ value, label }: { value: number; label: string }) => (
  <div className="flex flex-col items-center min-w-[68px] md:min-w-[92px] rounded-xl border border-gold/25 bg-gold/5 backdrop-blur-sm px-3 py-4 md:px-5 md:py-5">
    <span className="font-serif text-3xl md:text-5xl leading-none text-foreground tabular-nums">
      {pad(value)}
    </span>
    <span className="mt-2 text-[10px] md:text-xs uppercase tracking-[0.2em] text-muted-foreground">
      {label}
    </span>
  </div>
);

export const CountdownFull = () => {
  const { days, hours, minutes, seconds } = usePromoCountdown(1000);
  return (
    <div className="flex items-center justify-center gap-2 md:gap-4">
      <Cell value={days} label="Giorni" />
      <span className="font-serif text-2xl md:text-4xl text-gold/60">:</span>
      <Cell value={hours} label="Ore" />
      <span className="font-serif text-2xl md:text-4xl text-gold/60">:</span>
      <Cell value={minutes} label="Min" />
      <span className="font-serif text-2xl md:text-4xl text-gold/60">:</span>
      <Cell value={seconds} label="Sec" />
    </div>
  );
};
